import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faExclamationCircle,
  faPlus,
  faInfo,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import s from "./styles.scss";
import {
  hasError,
  getErrorMessage
} from "./SalesComissionsSportsProgramConfig.validations";

export interface ComisionLimit {
  numeroParticipantesDesde: number;
  numeroParticipantesHasta: number;
  montoPorParticipante: number;
  montoPorcentajeParticipante: number;
}

export interface TableProps {
  limits: ComisionLimit[];
  loading: boolean;
  tipoMonto: "PORCENTAJE" | "MONETARIO";
  onEdit: (index: number, limit: ComisionLimit) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
}

export const MemoTable = React.memo(
  ({ limits, loading, tipoMonto, onEdit, onDelete, onAdd }: TableProps) => {
    const [columns, setColumns] = useState(
      columnsFormatter(tipoMonto, limits, onEdit, onDelete, onAdd)
    );

    useEffect(() => {
      setColumns(columnsFormatter(tipoMonto, limits, onEdit, onDelete, onAdd));
    }, [tipoMonto, limits, onEdit, onDelete, onAdd]);

    return (
      <>
        <div className="row mb-4" style={{ marginLeft: "0" }}>
          <div>
            <ReactTable
              data={limits || []}
              columns={columns}
              loading={loading}
              className="-striped -highlight"
              showPagination={false}
              showPageSizeOptions={false}
              getTrProps={() => ({ style: { height: "40px" } })}
              sortable={false}
              minRows={5}
            />
          </div>
        </div>
      </>
    );
  }
);

function columnsFormatter(
  tipoMonto: "PORCENTAJE" | "MONETARIO",
  allLimits: ComisionLimit[],
  onEdit: (index: number, limit: ComisionLimit) => void,
  onDelete: (index: number) => void,
  onAdd: () => void
) {
  return [
    {
      Header: "Desde",
      id: "desde",
      width: 100,
      headerClassName: "text-left",
      Cell: (props: any) => {
        const row = props.original;
        const index = props.index;
        const error = hasError(row, index, allLimits, tipoMonto);
        return (
          <div
            className={s.field}
            style={{ color: error ? "#600" : "inherit" }}
          >
            {row.numeroParticipantesDesde}
          </div>
        );
      }
    },
    {
      Header: "Hasta",
      id: "hasta",
      width: 100,
      headerClassName: "text-left",
      Cell: (props: any) => {
        const row = props.original;
        const index = props.index;
        const error = hasError(row, index, allLimits, tipoMonto);
        const displayValue =
          row.numeroParticipantesHasta === -1
            ? "Sin límite"
            : row.numeroParticipantesHasta;
        return (
          <div
            className={s.field}
            style={{ color: error ? "#600" : "inherit" }}
          >
            {displayValue}
          </div>
        );
      }
    },
    {
      Header: tipoMonto === "PORCENTAJE" ? "Porcentaje (%)" : "Monto ($)",
      id: "monto",
      width: 120,
      headerClassName: "text-left",
      Cell: (props: any) => {
        const row = props.original;
        const index = props.index;
        const error = hasError(row, index, allLimits, tipoMonto);
        const porcentual = tipoMonto === "PORCENTAJE";
        const monto = porcentual
          ? row.montoPorcentajeParticipante
          : row.montoPorParticipante;
        const simboloPeso = porcentual ? "" : "$";
        const simboloPct = porcentual ? "%" : "";
        return (
          <div
            className={s.field}
            style={{ color: error ? "#600" : "inherit" }}
          >
            {simboloPeso}
            {monto.toLocaleString("es-MX", { minimumFractionDigits: 0 })}
            {simboloPct}
          </div>
        );
      }
    },
    {
      Header: () => (
        <div className="d-flex align-items-center justify-content-center">
          <span
            onClick={onAdd}
            style={{ cursor: "pointer" }}
            id="add-limit-btn"
          >
            <FontAwesomeIcon icon={faPlus} color="#000" />
            <UncontrolledTooltip placement="bottom" target="add-limit-btn">
              Agregar nuevo límite
            </UncontrolledTooltip>
          </span>
        </div>
      ),
      id: "acciones",
      width: 100,
      Cell: (props: any) => {
        const row = props.original;
        const index = props.index;
        const idEdit = `edit-limit-${index}`;
        const idDelete = `delete-limit-${index}`;
        const idError = `error-limit-${index}`;
        const errorMessage = getErrorMessage(row, index, allLimits, tipoMonto);

        return (
          <div className={`${s.field} text-center`}>
            <span className={s.btn} id={idError}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                color={errorMessage ? "#600" : "#1e7933ff"}
              />
              <UncontrolledTooltip placement="left" target={idError}>
                {errorMessage || "Ok"}
              </UncontrolledTooltip>
            </span>

            <span
              onClick={() => onEdit(index, row)}
              className={s.btn}
              id={idEdit}
            >
              <FontAwesomeIcon icon={faEdit} color="#0073e6" />
              <UncontrolledTooltip placement="bottom" target={idEdit}>
                Editar límite
              </UncontrolledTooltip>
            </span>

            <span
              onClick={() => onDelete(index)}
              className={s.btn}
              id={idDelete}
            >
              <FontAwesomeIcon icon={faTrash} color="#600" />
              <UncontrolledTooltip placement="bottom" target={idDelete}>
                Eliminar límite
              </UncontrolledTooltip>
            </span>
          </div>
        );
      }
    }
  ];
}
